/**
 * Sesion del usuario y sucursal activa.
 *
 * El token nunca pasa por aqui: vive en una cookie httpOnly que el navegador
 * manda solo. Este contexto guarda quien es el usuario, a que sucursales entra
 * y con que rol, para poder ocultar lo que no puede usar. La autorizacion real
 * la aplica el servidor en cada peticion.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  unitsForProfile,
  type Permission,
  type Role,
  type SessionUser,
  type Unit,
  type UnitProfile,
} from '@abarrotes/shared';
import { api, ApiError } from './api.js';
import { clearAllDrafts, DraftScopeProvider } from './draft.js';

const STORE_KEY = 'abarrotes.storeId';

interface MeResponse {
  user: SessionUser;
  permissionsByStore: Record<string, Permission[]>;
}

interface SessionValue {
  user: SessionUser | null;
  isLoading: boolean;
  storeId: number | null;
  storeName: string;
  role: Role | null;
  /**
   * Que se vende en la sucursal activa: tienda de abarrotes o bodega.
   *
   * Sirve para ESCONDER controles y para que un formulario no nazca proponiendo
   * una medida que la base va a rechazar. Quien de verdad lo aplica es un
   * trigger sobre `products`, igual que `can()` no es el control de acceso sino
   * su espejo.
   */
  unitProfile: UnitProfile;
  /** Las unidades de esa sucursal, ya resueltas. */
  units: readonly Unit[];
  setStoreId: (id: number) => void;
  can: (permission: Permission) => boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginError: ApiError | null;
  isLoggingIn: boolean;
  /** Fallo al leer la sesion que NO es falta de sesion. Ver `SessionProvider`. */
  sessionError: ApiError | null;
  reloadSession: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const [storeId, setStoreIdState] = useState<number | null>(() => {
    const saved = localStorage.getItem(STORE_KEY);
    const parsed = saved === null ? Number.NaN : Number(saved);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  });

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/auth/me'),
    /*
     * No tener sesion es una respuesta valida, no un fallo que reintentar. Un
     * fallo de RED si se reintenta, y esa distincion no es cosmetica: `App`
     * manda a la pantalla de acceso cuando no hay usuario, asi que con
     * `retry: false` para todo, un parpadeo de la conexion sacaba al cajero a
     * pedir contraseña a media venta sin que su sesion tuviera nada malo.
     */
    retry: (count, error) =>
      !(error instanceof ApiError && error.isAuthProblem) && count < 2,
    staleTime: 60_000,
  });

  const user = me.data?.user ?? null;

  /*
   * Solo los fallos que NO son de sesion. `UNAUTHENTICATED` significa "pide la
   * contraseña"; cualquier otro significa "no pude preguntar", y ahi mandar a
   * la pantalla de acceso seria decirle a alguien que salio cuando sigue dentro.
   */
  const sessionError = me.error instanceof ApiError && !me.error.isAuthProblem ? me.error : null;

  // La sucursal guardada puede haber dejado de estar asignada al usuario: si
  // ya no la tiene, se cae a la primera disponible en vez de dejar la app
  // pidiendo datos de una tienda a la que el servidor va a responder 403.
  useEffect(() => {
    if (!user) return;
    const available = user.stores.map((s) => s.storeId);
    if (available.length === 0) return;
    if (storeId === null || !available.includes(storeId)) {
      const first = available[0];
      if (first !== undefined) {
        setStoreIdState(first);
        localStorage.setItem(STORE_KEY, String(first));
      }
    }
  }, [user, storeId]);

  const setStoreId = useCallback(
    (id: number) => {
      setStoreIdState(id);
      localStorage.setItem(STORE_KEY, String(id));
      // Todo lo cacheado pertenece a la sucursal anterior.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const loginMutation = useMutation({
    mutationFn: (input: { username: string; password: string }) =>
      api<MeResponse>('/api/auth/login', { method: 'POST', body: input }),
    onSuccess: (data) => {
      queryClient.setQueryData(['me'], data);
      void queryClient.invalidateQueries();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
    onSettled: () => {
      queryClient.setQueryData(['me'], null);
      queryClient.clear();
      /*
       * Los borradores se van con la sesion, y no es limpieza: el renglon de un
       * ticket a medias carga `costCents`, y el costo no puede sobrevivir a la
       * sesion de quien podia verlo (§8). Quien sale en la all-in-one del
       * mostrador esta entregando la maquina.
       */
      clearAllDrafts();
    },
  });

  const membership = useMemo(
    () => user?.stores.find((s) => s.storeId === storeId) ?? null,
    [user, storeId],
  );

  const value = useMemo<SessionValue>(
    () => ({
      user,
      isLoading: me.isLoading,
      storeId: membership?.storeId ?? null,
      storeName: membership?.storeName ?? '',
      role: membership?.role ?? null,
      // Sin membresia se responde tienda de abarrotes, que es lo que era todo
      // hasta hoy. Ninguna pantalla interna se monta sin sucursal, asi que este
      // valor solo se lee mientras la sesion carga.
      unitProfile: membership?.unitProfile ?? 'retail',
      units: unitsForProfile(membership?.unitProfile ?? 'retail'),
      setStoreId,
      can: (permission) => (membership ? can(membership.role, permission) : false),
      login: async (username, password) => {
        await loginMutation.mutateAsync({ username, password });
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      loginError: loginMutation.error instanceof ApiError ? loginMutation.error : null,
      isLoggingIn: loginMutation.isPending,
      sessionError,
      reloadSession: () => void me.refetch(),
    }),
    [user, me.isLoading, me.refetch, sessionError, membership, setStoreId, loginMutation, logoutMutation],
  );

  /*
   * El ambito de los borradores cuelga de la sesion, no al reves.
   *
   * `lib/draft.tsx` no importa este archivo a proposito —seria un ciclo de
   * modulos, que compila callado y revienta en el navegador—: la sesion le pasa
   * quien es y en que tienda esta, y con eso el borrador queda marcado. Sin
   * usuario o sin sucursal el ambito es nulo y no se guarda nada, que es
   * preferible a escribir un borrador sin dueño.
   */
  return (
    <SessionContext.Provider value={value}>
      <DraftScopeProvider userId={user?.id ?? null} storeId={membership?.storeId ?? null}>
        {children}
      </DraftScopeProvider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession debe usarse dentro de SessionProvider');
  return value;
}

/**
 * Sucursal activa garantizada.
 *
 * Las pantallas internas solo se montan cuando ya hay sesion y sucursal, asi
 * que pueden pedir el id sin comprobar nulos en cada consulta.
 */
export function useStoreId(): number {
  const { storeId } = useSession();
  if (storeId === null) throw new Error('no hay sucursal activa');
  return storeId;
}
